import {
  fmt,
  fmtDateShort,
  presetRange,
  type DayRange,
  type RangePreset,
  type SaleJournal,
} from '@k2pos/core';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Mono, Text } from '@/components/ui/Text';
import { cx } from '@/lib/cx';
import { useGoBack } from '@/hooks/useNav';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Orders.module.css';

const PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'today', label: 'ມື້ນີ້' },
  { key: 'week', label: '7 ມື້' },
  { key: 'month', label: 'ເດືອນນີ້' },
];

/**
 * Every bill the shop has issued.
 *
 * Until now an invoice was visible only on the sale-done screen: leaving it made
 * the bill unreachable forever, even though the sale itself was safely recorded.
 * This reads the journal directly, which is the cheapest demonstration that the
 * ledger works.
 */
export function Orders() {
  const goBack = useGoBack('/more');
  const journal = usePos((s) => s.journal);
  const journalStartedAt = usePos((s) => s.journalStartedAt);
  const [preset, setPreset] = useState<RangePreset>('week');
  const [query, setQuery] = useState('');

  const range: DayRange = useMemo(() => presetRange(preset), [preset]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return journal
      .filter((e): e is SaleJournal => e.kind === 'sale')
      .filter((e) => e.day >= range.from && e.day <= range.to)
      .filter(
        (e) =>
          !q ||
          e.invoiceNo.toLowerCase().includes(q) ||
          (e.customerName ?? '').toLowerCase().includes(q),
      )
      .reverse(); // newest first
  }, [journal, range, query]);

  const total = rows.reduce((sum, r) => sum + r.total, 0);

  return (
    <>
      <AppBar
        title="ອໍເດີ້"
        onBack={goBack}
        subtitle={
          <Text size={11} color={colors.onDarkSoft}>
            {rows.length} ບິນ ·{' '}
            <Mono weight={700} size={13} color={colors.sky}>
              {fmt(total)}
            </Mono>{' '}
            ₭
          </Text>
        }
      />

      <div className={styles.controls}>
        <Field
          value={query}
          onChange={setQuery}
          placeholder="ຄົ້ນຫາເລກບິນ ຫຼື ຊື່ລູກຄ້າ..."
          label="ຄົ້ນຫາອໍເດີ້"
        />
        <div className={styles.presets} role="tablist">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={p.key === preset}
              onClick={() => setPreset(p.key)}
              className={cx(styles.chip, p.key === preset && styles.chipOn)}
            >
              <Text
                weight={p.key === preset ? 800 : 500}
                size={11.5}
                color={p.key === preset ? colors.surfaceAlt : colors.textMuted}
              >
                {p.label}
              </Text>
            </button>
          ))}
        </div>
      </div>

      <div className={cx('vscroll', styles.list)}>
        {rows.map((row) => (
          <Link key={row.id} to={`/orders/${row.id}`} className={styles.rowLink}>
            <Card
              accent={row.payMode === 'credit' ? colors.skyDeep : colors.navy}
              className={styles.row}
            >
              <div className="grow col">
                <Text weight={700} size={13.5} lines={1}>
                  {row.customerName ?? 'ລູກຄ້າທົ່ວໄປ'}
                </Text>
                <Mono size={10.5} color={colors.textMuted}>
                  #{row.invoiceNo} · {fmtDateShort(new Date(row.at))}
                </Mono>
              </div>

              <div className={styles.rowRight}>
                <Mono weight={700} size={14} className="selectable">
                  {fmt(row.total)}
                </Mono>
                <span
                  className={cx(styles.pill, row.payMode === 'credit' && styles.pillCredit)}
                >
                  <Text
                    weight={700}
                    size={9.5}
                    color={row.payMode === 'credit' ? colors.sky : colors.textMuted}
                  >
                    {row.payMode === 'credit' ? 'ຕິດໜີ້' : 'ຈ່າຍເລີຍ'}
                  </Text>
                </span>
              </div>
            </Card>
          </Link>
        ))}

        {rows.length === 0 ? (
          <div className={styles.empty}>
            <Text size={12.5} color={colors.textMuted} align="center">
              ບໍ່ມີບິນໃນຊ່ວງນີ້
            </Text>
            {/* An existing device starts its ledger on the day it updated, so
                say that rather than implying the shop sold nothing. */}
            <Mono size={10.5} color={colors.textFaint} align="center">
              ຂໍ້ມູນເລີ່ມແຕ່ {journalStartedAt}
            </Mono>
          </div>
        ) : null}
      </div>
    </>
  );
}
